import { myApi } from "./api";

// Helpers to keep the endpoint list short
const list = (url, tag, extra = {}) => ({
  query: (params) => ({ url, params: clean(params) }),
  transformResponse: (r) => ({ items: r?.data ?? [], pagination: r?.pagination, counts: r?.counts, summary: r?.summary, gateway: r?.gateway }),
  providesTags: [tag],
  ...extra,
});
const one = (fn, tag) => ({ query: fn, transformResponse: (r) => r?.data, providesTags: Array.isArray(tag) ? tag : [tag] });
const mut = (fn, tags) => ({ query: fn, invalidatesTags: tags });
const clean = (p) => {
  const o = {};
  Object.entries(p || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") o[k] = v;
  });
  return o;
};

export const adminApi = myApi.injectEndpoints({
  endpoints: (b) => ({
    // account
    login: b.mutation({ query: (body) => ({ url: "/login", method: "POST", body }) }),
    me: b.query(one(() => "/me", "me")),
    updateProfile: b.mutation(mut((body) => ({ url: "/profile", method: "PUT", body }), ["me"])),
    changePassword: b.mutation({ query: (body) => ({ url: "/change-password", method: "POST", body }) }),

    // dashboard
    dashboard: b.query(one((range) => ({ url: "/get-dashboard", params: { range } }), "dashboard")),

    // orders & payments
    orders: b.query(list("/orders", "orders")),
    order: b.query(one((id) => `/order/${id}`, "order")),
    updateOrderStatus: b.mutation(mut((body) => ({ url: "/update-order-status", method: "PUT", body }), ["orders", "order", "dashboard", "inventory", "products"])),
    updateOrderNote: b.mutation(mut(({ id, admin_note }) => ({ url: `/order/${id}/note`, method: "PUT", body: { admin_note } }), ["order"])),
    refundOrder: b.mutation(mut(({ id, ...body }) => ({ url: `/order/${id}/refund`, method: "POST", body }), ["orders", "order", "dashboard", "transactions", "inventory", "products"])),
    transactions: b.query(list("/transactions", "transactions")),

    // customers
    customers: b.query(list("/get-users", "customers")),
    customer: b.query(one((id) => `/customer/${id}`, "customers")),
    toggleCustomer: b.mutation(mut((id) => ({ url: `/customer-status/${id}`, method: "PUT" }), ["customers"])),
    subscribers: b.query(list("/subscribers", "subscribers")),
    deleteSubscriber: b.mutation(mut((id) => ({ url: `/subscriber/${id}`, method: "DELETE" }), ["subscribers"])),

    // catalogue
    mainCategories: b.query(one(() => "/get-main-category", "categories")),
    categories: b.query(one(() => "/get-category", "categories")),
    subCategories: b.query(one(() => "/get-sub-category", "categories")),
    saveMainCategory: b.mutation(mut((body) => ({ url: body.id ? "/update-main-category" : "/insert-main-category", method: body.id ? "PUT" : "POST", body }), ["categories"])),
    saveCategory: b.mutation(mut((body) => ({ url: body.id ? "/update-category" : "/insert-category", method: body.id ? "PUT" : "POST", body }), ["categories", "products"])),
    saveSubCategory: b.mutation(mut((body) => ({ url: body.id ? "/update-sub-category" : "/insert-sub-category", method: body.id ? "PUT" : "POST", body }), ["categories", "products"])),
    toggleCategory: b.mutation(mut(({ level, id }) => ({ url: `/update-${level}-status/${id}`, method: "PUT" }), ["categories"])),
    deleteCategory: b.mutation(mut(({ level, id }) => ({ url: `/${level}/${id}`, method: "DELETE" }), ["categories"])),

    products: b.query(list("/get-products", "products")),
    product: b.query(one((id) => `/get-product-by-id/${id}`, "products")),
    saveProduct: b.mutation(mut(({ id, form }) => ({ url: id ? "/update-products" : "/insert-product", method: id ? "PUT" : "POST", body: form }), ["products", "inventory", "dashboard"])),
    deleteProduct: b.mutation(mut((id) => ({ url: `/product/${id}`, method: "DELETE" }), ["products", "inventory", "homeTitle", "dashboard"])),
    productFlags: b.mutation(mut(({ id, ...body }) => ({ url: `/product/${id}/flags`, method: "PUT", body }), ["products"])),
    updateStock: b.mutation(mut(({ id, ...body }) => ({ url: `/product/${id}/stock`, method: "PUT", body }), ["products", "inventory", "dashboard"])),
    inventory: b.query(list("/inventory", "inventory")),
    inventoryLog: b.query(one((id) => `/product/${id}/inventory-log`, "inventory")),

    // content
    banners: b.query(one(() => "/get-banner", "banners")),
    addBanner: b.mutation(mut((form) => ({ url: "/insert-banner", method: "POST", body: form }), ["banners"])),
    updateBanner: b.mutation(mut(({ id, form }) => ({ url: `/banner/${id}`, method: "PUT", body: form }), ["banners"])),
    toggleBanner: b.mutation(mut(({ id, show_banner }) => ({ url: `/update-banner/${id}/${show_banner}`, method: "PUT" }), ["banners"])),
    deleteBanner: b.mutation(mut((id) => ({ url: `/delete-banner/${id}`, method: "DELETE" }), ["banners"])),

    homeTitles: b.query(one(() => "/get-home-title", "homeTitle")),
    saveHomeTitle: b.mutation(mut((body) => ({ url: body.id ? "/update-home-title" : "/insert-home-title", method: body.id ? "PUT" : "POST", body }), ["homeTitle"])),
    toggleHomeTitle: b.mutation(mut((id) => ({ url: `/update-home-title-staus/${id}`, method: "PUT" }), ["homeTitle"])),
    deleteHomeTitle: b.mutation(mut((id) => ({ url: `/delete-home-title/${id}`, method: "DELETE" }), ["homeTitle"])),
    setHomeProducts: b.mutation(mut((body) => ({ url: "/add-home-product", method: "POST", body }), ["homeTitle"])),

    explore: b.query(one(() => "/get-explore", "explore")),
    addExplore: b.mutation(mut((form) => ({ url: "/insert-explore", method: "POST", body: form }), ["explore"])),
    toggleExplore: b.mutation(mut((id) => ({ url: `/update-explore/${id}`, method: "PUT" }), ["explore"])),
    deleteExplore: b.mutation(mut((id) => ({ url: `/delete-explore/${id}`, method: "DELETE" }), ["explore"])),

    faqs: b.query(one(() => "/get-faq", "faq")),
    saveFaq: b.mutation(mut((body) => ({ url: body.id ? "/update-faq" : "/add-faq", method: body.id ? "PUT" : "POST", body }), ["faq"])),
    toggleFaq: b.mutation(mut((id) => ({ url: `/update-faq-status/${id}`, method: "PUT" }), ["faq"])),
    deleteFaq: b.mutation(mut((id) => ({ url: `/delete-faq/${id}`, method: "DELETE" }), ["faq"])),

    policy: b.query(one(() => "/get-sitepolicy", "policy")),
    savePolicy: b.mutation(mut((body) => ({ url: "/add-update-sitepolicy", method: "POST", body }), ["policy"])),

    socials: b.query(one(() => "/get-socialmedia", "social")),
    saveSocial: b.mutation(mut((body) => ({ url: body.id ? "/update-socialmedia" : "/add-socialmedia", method: body.id ? "PUT" : "POST", body }), ["social"])),
    deleteSocial: b.mutation(mut((id) => ({ url: `/delete-socialmedia/${id}`, method: "DELETE" }), ["social"])),

    // marketing
    coupons: b.query(one(() => "/coupons", "coupons")),
    saveCoupon: b.mutation(mut(({ id, ...body }) => ({ url: id ? `/coupon/${id}` : "/coupon", method: id ? "PUT" : "POST", body }), ["coupons"])),
    toggleCoupon: b.mutation(mut((id) => ({ url: `/coupon/${id}/status`, method: "PUT" }), ["coupons"])),
    deleteCoupon: b.mutation(mut((id) => ({ url: `/coupon/${id}`, method: "DELETE" }), ["coupons"])),

    reviews: b.query(list("/reviews", "reviews")),
    setReviewStatus: b.mutation(mut(({ id, status }) => ({ url: `/review/${id}/status`, method: "PUT", body: { status } }), ["reviews", "dashboard", "products"])),
    deleteReview: b.mutation(mut((id) => ({ url: `/review/${id}`, method: "DELETE" }), ["reviews", "dashboard"])),

    // shipping & settings
    shippingMethods: b.query(one(() => "/shipping-methods", "shipping")),
    saveShipping: b.mutation(mut(({ id, ...body }) => ({ url: id ? `/shipping-method/${id}` : "/shipping-method", method: id ? "PUT" : "POST", body }), ["shipping"])),
    deleteShipping: b.mutation(mut((id) => ({ url: `/shipping-method/${id}`, method: "DELETE" }), ["shipping"])),
    settings: b.query(one(() => "/settings", "settings")),
    saveSettings: b.mutation(mut((body) => ({ url: "/settings", method: "PUT", body }), ["settings"])),

    // staff
    staff: b.query(one(() => "/get-role", "staff")),
    saveStaff: b.mutation(mut((body) => ({ url: body.id ? "/update-role" : "/add-role", method: body.id ? "PUT" : "POST", body }), ["staff"])),
    deleteStaff: b.mutation(mut((id) => ({ url: `/delete-role/${id}`, method: "DELETE" }), ["staff"])),
  }),
});

export const api = adminApi;
export const {
  useLoginMutation,
  useMeQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useDashboardQuery,
  useOrdersQuery,
  useOrderQuery,
  useUpdateOrderStatusMutation,
  useUpdateOrderNoteMutation,
  useRefundOrderMutation,
  useTransactionsQuery,
  useCustomersQuery,
  useCustomerQuery,
  useToggleCustomerMutation,
  useSubscribersQuery,
  useDeleteSubscriberMutation,
  useMainCategoriesQuery,
  useCategoriesQuery,
  useSubCategoriesQuery,
  useSaveMainCategoryMutation,
  useSaveCategoryMutation,
  useSaveSubCategoryMutation,
  useToggleCategoryMutation,
  useDeleteCategoryMutation,
  useProductsQuery,
  useProductQuery,
  useSaveProductMutation,
  useDeleteProductMutation,
  useProductFlagsMutation,
  useUpdateStockMutation,
  useInventoryQuery,
  useInventoryLogQuery,
  useBannersQuery,
  useAddBannerMutation,
  useUpdateBannerMutation,
  useToggleBannerMutation,
  useDeleteBannerMutation,
  useHomeTitlesQuery,
  useSaveHomeTitleMutation,
  useToggleHomeTitleMutation,
  useDeleteHomeTitleMutation,
  useSetHomeProductsMutation,
  useExploreQuery,
  useAddExploreMutation,
  useToggleExploreMutation,
  useDeleteExploreMutation,
  useFaqsQuery,
  useSaveFaqMutation,
  useToggleFaqMutation,
  useDeleteFaqMutation,
  usePolicyQuery,
  useSavePolicyMutation,
  useSocialsQuery,
  useSaveSocialMutation,
  useDeleteSocialMutation,
  useCouponsQuery,
  useSaveCouponMutation,
  useToggleCouponMutation,
  useDeleteCouponMutation,
  useReviewsQuery,
  useSetReviewStatusMutation,
  useDeleteReviewMutation,
  useShippingMethodsQuery,
  useSaveShippingMutation,
  useDeleteShippingMutation,
  useSettingsQuery,
  useSaveSettingsMutation,
  useStaffQuery,
  useSaveStaffMutation,
  useDeleteStaffMutation,
} = adminApi;
